
/**
 * Main entry point for the Vue 3 application
 * Responsible for:
 * - Initializing Vue app and plugins
 * - Setting up Pinia store
 * - Configuring Vuetify
 * - Initializing Keycloak authentication
 * - Mounting the app once authentication is confirmed
 * - Loading SPA configurations for single-spa integration
 * - Registering global filters
 *  
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-12
 */

import { createApp } from "vue"
import App from "./App.vue"
import router from "./lib/router/router"
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import keycloak from "./lib/plugins/keycloak-config"
import { hubSpotChat } from "./lib/plugins/hubspotChat"
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import LiquidskyUI from '@maropost-ui/liquidsky-ui'
import {handleOAuthRedirect} from "@/lib/plugins/facebookOauth"
import { registerDocumentTitleBridge } from '@/lib/utils/documentTitle'

// Create Vue app instance
const app = createApp(App)

// Initialize Pinia store
const pinia = createPinia()

// Hubspot Chat
const hubspotChat = hubSpotChat()

// Register pinia-plugin-persistedstate
pinia.use(piniaPluginPersistedstate)
// handle meta integration redirection
handleOAuthRedirect()

// Register plugins
app.use(router)
app.use(LiquidskyUI)
app.use(pinia)
app.use(hubspotChat)
registerDocumentTitleBridge()

/* ---------------------------------------
   SERVICE WORKER REGISTRATION
--------------------------------------- */

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js")
      .then((registration) => {
        console.log("Service Worker registered:", registration);
      })
      .catch((error) => {
        console.error("Service Worker registration failed:", error);
      });
  });
}

/**
 * Keycloak authentication initialization
 * - onLoad: "check-sso" (check existing session, do not force login)
 * - checkLoginIframe: false (disable periodic iframe check)
 */

keycloak.init({
  onLoad: "check-sso",
  checkLoginIframe: false
})
.then(async(authenticated: boolean) => {

  // If user is not authenticated, redirect to Keycloak login page
  if (!authenticated) {
    keycloak.login()
    return
  }

  // Register CKEditor globally
  window.CKEditor = ClassicEditor

   /**
 * Initialize Amplitude Analytics and Session Replay once, client-side only.
 * Per docs: import at root, initAll with analytics autocapture + sessionReplay.
 */
   const amplitude = await import('@amplitude/analytics-browser');
   const engagement = await import("@amplitude/engagement-browser");

   const apiKey = (process.env.VUE_APP_AMPLITUDE_API_KEY ?? '').trim()
   if (apiKey) {
    amplitude.add(engagement.plugin());
    amplitude.init(apiKey, {
       defaultTracking: false,
     })
   } 

  // Mount Vue app only after authentication
  app.mount("#app")
})
.catch((err: unknown) => {
  console.error("Auth service initialization failed.", err)
  // Handle Keycloak initialization failure
    // Prepare the query object with error details
    let query = {};
    if (err) {
      query = (err as any).error ? {
        error: (err as any).error, 
        error_description: (err as any).error_description 
      } : {};
    }
    app.mount("#app")
    setTimeout(() => {
        // Redirect to authentication page with error details
        router.replace({
          path: '/authentication', 
          query 
        })
    });
})

export {keycloak , pinia}